import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NbAuthOAuth2JWTToken, NbAuthToken, NbTokenService } from '@nebular/auth';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, take, timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * Borne haute pour `/api/token/refresh/`.
 *
 * Indispensable depuis que le refresh est partagé : toutes les requêtes de
 * l'application attendent désormais le même appel. Si le réseau avale la requête
 * sans jamais répondre, sans cette borne l'application entière resterait figée
 * jusqu'au timeout TCP de l'OS (plusieurs minutes).
 */
const REFRESH_TIMEOUT_MS = 15000;

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  currentToken!: NbAuthToken | undefined;
  isRefreshing = false;
  private tokenIsValid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Refresh en cours, partagé entre tous les appelants concurrents.
   *
   * Sans ce partage, les requêtes qui partaient en parallèle pendant un refresh
   * (l'éditeur en lance une dizaine au chargement) recevaient un observable qui
   * n'émettait qu'une valeur périmée et ne complétait jamais — voir verify().
   */
  private refreshInFlight$: Observable<boolean> | null = null;

  /**
   * Incrémenté à chaque `clearToken()`. Un refresh démarré avant une
   * déconnexion ne doit pas ré-écrire un token dans le localStorage quand sa
   * réponse arrive : c'est ce genre d'état incohérent qui survivait au
   * rechargement de la page et obligeait à vider le stockage du navigateur.
   */
  private authEpoch = 0;

  /** Identifie le refresh courant, pour que seul lui puisse libérer son verrou. */
  private refreshGeneration = 0;

  constructor(private http: HttpClient, private tokenService: NbTokenService) {


    this.tokenService.tokenChange().subscribe((token) => {

      this.currentToken = token;
      this.tokenIsValid.next(this.currentToken?.isValid() || false);
    });

    // Initialiser le token stocké
    this.tokenService.get().subscribe((token) => {

      if (token instanceof NbAuthOAuth2JWTToken) {
        this.currentToken = token;
      } else {
        const storedTokenString = localStorage.getItem('auth_app_token');
        if (storedTokenString) {
          const storedToken = JSON.parse(storedTokenString);
          this.currentToken = new NbAuthOAuth2JWTToken(storedToken, 'email');
        }
      }
      this.tokenIsValid.next(this.currentToken?.isValid() || false);
    });
  }

  /**
   * Rafraîchit le token d'accès. Les appels concurrents partagent la même
   * requête HTTP et reçoivent tous le même résultat.
   *
   * L'observable retourné émet TOUJOURS exactement une valeur puis complète,
   * y compris en cas d'échec (`false`).
   */
  refresh(): Observable<boolean> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const refreshToken = this.currentToken?.getPayload()?.refresh_token;
    if (!refreshToken) {
      this.tokenIsValid.next(false);
      return of(false);
    }

    const epoch = this.authEpoch;
    const generation = ++this.refreshGeneration;
    this.isRefreshing = true;
    this.refreshInFlight$ = this.http
      .post<{ access: string }>(`${environment.backendUrl}/api/token/refresh/`, { refresh: refreshToken })
      .pipe(
        timeout(REFRESH_TIMEOUT_MS),
        map((response) => {
          // Déconnexion survenue pendant la requête : on ne restaure rien.
          if (epoch !== this.authEpoch) {
            return false;
          }

          if (!response?.access) {
            this.tokenIsValid.next(false);
            return false;
          }

          const newToken = new NbAuthOAuth2JWTToken(
            { access_token: response.access, refresh_token: refreshToken },
            'email',
          );
          this.tokenService.set(newToken).subscribe();
          this.tokenIsValid.next(true);
          return true;
        }),
        catchError((error) => {
          console.error('Refresh Error:', error);
          this.tokenIsValid.next(false);
          return of(false);
        }),
        // Le verrou est libéré quoi qu'il arrive : succès, échec ou erreur.
        // Auparavant il n'était relâché que dans le `map` de succès, donc un
        // refresh en erreur laissait `isRefreshing` à true pour toute la vie du SPA.
        //
        // Le test de génération évite qu'un refresh tardif n'efface le handle
        // d'un refresh plus récent (cas : déconnexion puis reconnexion pendant
        // qu'une première requête est encore en vol).
        finalize(() => {
          if (this.refreshGeneration !== generation) return;
          this.refreshInFlight$ = null;
          this.isRefreshing = false;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    return this.refreshInFlight$;
  }

  /**
   * Retourne true si le token courant est utilisable, en tentant un refresh si
   * nécessaire.
   *
   * Cet observable émet exactement une fois puis complète. C'est essentiel :
   * l'intercepteur l'enchaîne avec `mergeMap` sur chaque requête HTTP, et les
   * appelants qui utilisent `firstValueFrom`/`lastValueFrom` attendent sa
   * complétion. L'ancienne implémentation renvoyait, en cas de refresh déjà en
   * cours, le BehaviorSubject `tokenIsValid` : il émettait immédiatement sa
   * valeur périmée (`false`), ne complétait jamais, et ré-émettait à chaque
   * changement de token — ce qui rejouait le `mergeMap` de toutes les requêtes en
   * attente, chacune rappelant `clearToken()`, ce qui provoquait une nouvelle
   * émission : boucle auto-entretenue qui figeait l'onglet.
   */
  verify(): Observable<boolean> {
    if (this.currentToken?.isValid()) {
      return of(true);
    }

    return this.refresh().pipe(take(1));
  }

  clearToken() {
    this.authEpoch++;
    this.currentToken = undefined;
    this.isRefreshing = false;
    this.refreshInFlight$ = null;
    this.tokenService.clear().subscribe(() => {
    });
  }
}
